import { differenceInCalendarDays } from 'date-fns';

import type { OpenedCasesWidgetData } from '../types';

export type OpenedCasesQuery = {
  /** ISO yyyy-MM-dd, inclusive. */
  from: string;
  /** ISO yyyy-MM-dd, inclusive. */
  to: string;
};

/** Shaped like a future API response so a real fetch can drop in without changing call sites. */
export type OpenedCasesResponse = {
  data: OpenedCasesWidgetData;
};

export const OPENED_CASES_SERIES: OpenedCasesWidgetData['series'] = [
  { color: '#000', id: 'offer', label: 'Off.' },
  { color: '#B51E1F', id: 'withdrawn', label: 'Wh.' },
  { color: '#8D8D92', id: 'domestic', label: 'Dom.' },
  { color: '#DEE1E5', id: 'international', label: 'Int.' },
  { color: '#E28A8A', id: 'return', label: 'Ret.' },
];

const CATEGORY_WAVES = [
  { amplitude: 3, base: 13, id: 'offer', phase: 0 },
  { amplitude: 2, base: 5, id: 'withdrawn', phase: 1.1 },
  { amplitude: 3, base: 12, id: 'domestic', phase: 2.4 },
  { amplitude: 2, base: 9, id: 'international', phase: 3.6 },
  { amplitude: 2, base: 3, id: 'return', phase: 4.8 },
];

function toDayLabel(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getDate()}.${month}`;
}

/** Deterministic mock series so the chart looks the same on every render. */
export function generateOpenedCasesData({ from, to }: OpenedCasesQuery): OpenedCasesResponse {
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  const days = Math.max(0, differenceInCalendarDays(end, start) + 1);

  const labels: string[] = [];
  const values: OpenedCasesWidgetData['values'] = [];

  for (let index = 0; index < days; index += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    labels.push(toDayLabel(date));

    // Days since epoch, not `index` — so ranges of equal length (e.g. "this
    // week" vs "last week") still produce different values per date.
    const dayOffset = Math.floor(date.getTime() / 86_400_000);

    const record: Record<string, number> = {};
    CATEGORY_WAVES.forEach(({ amplitude, base, id, phase }) => {
      const wave = Math.sin(dayOffset * 0.5 + phase) * amplitude;
      record[id] = Math.max(1, Math.round(base + wave));
    });
    values.push(record);
  }

  return {
    data: {
      labels,
      series: OPENED_CASES_SERIES,
      values,
    },
  };
}
